// The built-in warm-up and cool-down routines: small, 3 to 5 minutes each (decision 2026-10-14), made from catalog
// exercises. A workout suggests one of each by id (Workout.warmupId, cooldownId); the person can pick another one
// or switch it off on the session start screen. Ids are stable: preferences and history point at them.
// Block ids follow the section ("warmup_1", "cooldown_1"), like the warm-ups that were part of the workouts before.
import type { Block, Equipment } from '../types';
import { ids, reps, t } from '../workouts/plan';

export type RoutineKind = 'warmup' | 'cooldown';

export interface Routine {
  /** Stable, ASCII snake_case, unique within its kind. */
  id: string;
  kind: RoutineKind;
  name: string;
  /** One sentence for the picker. */
  description: string;
  /** What it needs besides the floor: 'none' or 'bodyweight' for most. */
  equipment: Equipment[];
  blocks: Block[];
}

const warmup = (id: string, name: string, description: string, equipment: Equipment[], blocks: Omit<Block, 'id'>[]): Routine => ({
  id, kind: 'warmup', name, description, equipment, blocks: ids('warmup', blocks),
});
const cooldown = (id: string, name: string, description: string, equipment: Equipment[], blocks: Omit<Block, 'id'>[]): Routine => ({
  id, kind: 'cooldown', name, description, equipment, blocks: ids('cooldown', blocks),
});

export const WARMUPS: Routine[] = [
  // The warm-up of the starter workout Kettlebell Full Body (owner's choice, 2026-10-11), unchanged.
  warmup('general', 'General full body', 'Shoulders, hips, squats, bridges and the spine: fits almost any workout.', ['bodyweight'], [
    { exerciseId: 'arm_circles', amount: t(30, true), note: 'per direction' },
    { exerciseId: 'hip_flexor_hamstring_stretch', amount: t(30, true) },
    { exerciseId: 'squat', amount: reps(20) },
    { exerciseId: 'glute_bridge', amount: reps(10, { max: 15 }) },
    { exerciseId: 'cat_cow', amount: t(30) },
  ]),
  warmup('kettlebell', 'Kettlebell', 'Shoulders and hips, then halos, goblet squats, deadlifts and a few easy swings with a light bell.', ['kettlebell'], [
    { exerciseId: 'arm_circles', amount: t(30, true), note: 'per direction' },
    { exerciseId: 'hip_circles', amount: t(30) },
    { exerciseId: 'kettlebell_halo', amount: reps(5, { perSide: true }), note: 'per direction' },
    { exerciseId: 'goblet_squat', amount: reps(8), note: 'slow, pry the knees open at the bottom' },
    { exerciseId: 'kettlebell_deadlift', amount: reps(10) },
    { exerciseId: 'kettlebell_swing', amount: reps(10), note: 'light, find the rhythm' },
  ]),
  warmup('legs', 'Legs', 'Pulse up, swing the legs loose, then squats, lunges and bridges for a leg day.', ['bodyweight'], [
    { exerciseId: 'marching_high_knees', amount: t(45) },
    { exerciseId: 'leg_swings_front', amount: t(20, true) },
    { exerciseId: 'leg_swings_side', amount: t(20, true) },
    { exerciseId: 'hip_circles', amount: t(30) },
    { exerciseId: 'squat', amount: reps(12) },
    { exerciseId: 'reverse_lunge', amount: reps(5, { perSide: true }) },
    { exerciseId: 'glute_bridge', amount: reps(10) },
  ]),
  warmup('upper_body', 'Upper body', 'Shoulders, upper back and wrists, then shoulder-blade push-ups and a few easy push-ups.', ['bodyweight'], [
    { exerciseId: 'jumping_jack', amount: t(30) },
    { exerciseId: 'arm_circles', amount: t(30, true), note: 'per direction' },
    { exerciseId: 'shoulder_rolls', amount: t(30) },
    { exerciseId: 'wall_slides', amount: t(30) },
    { exerciseId: 'torso_twists', amount: t(30) },
    { exerciseId: 'scapular_push_up', amount: reps(10) },
    { exerciseId: 'push_up', amount: reps(8), note: 'easy, on the knees is fine' },
  ]),
  warmup('running_drills', 'Running drills', 'Jog, leg swings and ankles, then short A-skips, butt kicks and pogo hops before a run, a ride or rope work.', ['bodyweight'], [
    { exerciseId: 'jog_in_place', amount: t(60) },
    { exerciseId: 'leg_swings_front', amount: t(15, true) },
    { exerciseId: 'leg_swings_side', amount: t(15, true) },
    { exerciseId: 'ankle_rocks', amount: t(15, true) },
    { exerciseId: 'a_skip', amount: t(20) },
    { exerciseId: 'butt_kicks', amount: t(20) },
    { exerciseId: 'pogo_hops', amount: t(20) },
  ]),
  warmup('mobility', 'Mobility', 'Slow joint work on the floor: spine, hips and ankles. Good before core work or when stiff.', ['none'], [
    { exerciseId: 'cat_cow', amount: t(30) },
    { exerciseId: 'worlds_greatest_stretch', amount: t(30, true) },
    { exerciseId: 'thread_the_needle', amount: t(20, true) },
    { exerciseId: 'ninety_ninety_switch', amount: t(30) },
    { exerciseId: 'deep_squat_hold', amount: t(30) },
    { exerciseId: 'ankle_rocks', amount: t(15, true) },
  ]),
  warmup('desk_break', 'Short (3 min)', 'Three standing minutes, desk-break style: neck, shoulders, spine, hips and a few squats.', ['none'], [
    { exerciseId: 'neck_rolls', amount: t(20) },
    { exerciseId: 'shoulder_rolls', amount: t(30) },
    { exerciseId: 'arm_circles', amount: t(15, true), note: 'per direction' },
    { exerciseId: 'torso_twists', amount: t(30) },
    { exerciseId: 'hip_circles', amount: t(30) },
    { exerciseId: 'squat', amount: reps(10) },
    { exerciseId: 'marching_high_knees', amount: t(20) },
  ]),
  // The gym warm-up used to start with 5 minutes of rowing (8 in all); shortened to fit the 5 minute limit.
  warmup('gym', 'Gym: rower and patterns', 'Two easy minutes on the rower, then hips, squats and push-ups. Ramp-up sets stay in the workout.', ['machine'], [
    { exerciseId: 'rowing_machine', amount: t(120), note: 'easy pace' },
    { exerciseId: 'arm_circles', amount: t(15, true), note: 'per direction' },
    { exerciseId: 'worlds_greatest_stretch', amount: t(20, true) },
    { exerciseId: 'squat', amount: reps(10) },
    { exerciseId: 'push_up', amount: reps(8) },
  ]),
];

export const COOLDOWNS: Routine[] = [
  // The cool-down of the starter workout Kettlebell Full Body (same exercises and block ids; breathing 45 s instead of 60,
  // so it fits 5 minutes with the side switches).
  cooldown('full_body', 'Full-body stretch', 'Shoulders, hips, hamstrings and quads, then slow breathing.', ['none'], [
    { exerciseId: 'downward_dog', amount: t(30) },
    { exerciseId: 'pigeon_stretch', amount: t(30, true) },
    { exerciseId: 'hip_flexor_hamstring_stretch', amount: t(30, true) },
    { exerciseId: 'standing_quad_stretch', amount: t(30, true) },
    { exerciseId: 'puppy_pose', amount: t(30) },
    { exerciseId: 'deep_breathing', amount: t(45) },
  ]),
  cooldown('legs_hips', 'Legs and hips', 'Quads, glutes, hamstrings and calves after a leg day.', ['none'], [
    { exerciseId: 'standing_quad_stretch', amount: t(30, true) },
    { exerciseId: 'figure_four_stretch', amount: t(30, true) },
    { exerciseId: 'standing_hamstring_stretch', amount: t(30, true) },
    { exerciseId: 'standing_calf_stretch', amount: t(30, true) },
    { exerciseId: 'deep_breathing', amount: t(40) },
  ]),
  cooldown('upper_back', 'Upper body and back', 'Chest, shoulders, triceps and the back after pushing and pulling.', ['none'], [
    { exerciseId: 'doorway_chest_stretch', amount: t(30, true) },
    { exerciseId: 'cross_body_shoulder_stretch', amount: t(30, true) },
    { exerciseId: 'overhead_triceps_stretch', amount: t(30, true) },
    { exerciseId: 'childs_pose', amount: t(45) },
    { exerciseId: 'deep_breathing', amount: t(60) },
  ]),
  cooldown('breathing', 'Breathing and calm down', 'Lie down, let the heart rate drop: easy stretches and slow breathing.', ['none'], [
    { exerciseId: 'childs_pose', amount: t(45) },
    { exerciseId: 'supine_twist', amount: t(30, true) },
    { exerciseId: 'legs_up_the_wall', amount: t(60) },
    { exerciseId: 'box_breathing', amount: t(60) },
    { exerciseId: 'belly_breathing', amount: t(60) },
  ]),
  cooldown('runners', "Runner's stretch", 'Standing stretches for quads, hip flexors, hamstrings and calves after running or rope work.', ['none'], [
    { exerciseId: 'standing_quad_stretch', amount: t(30, true) },
    { exerciseId: 'kneeling_hip_flexor_stretch', amount: t(30, true) },
    { exerciseId: 'standing_hamstring_stretch', amount: t(30, true) },
    { exerciseId: 'standing_calf_stretch', amount: t(30, true) },
    { exerciseId: 'deep_breathing', amount: t(40) },
  ]),
];

export const ROUTINES: Routine[] = [...WARMUPS, ...COOLDOWNS];
